import React, { useEffect, useState } from 'react';
import emailConfigService from '../services/emailConfig';

const LABELS = {
    tenant_id: 'Directory (tenant) ID',
    client_id: 'Client ID',
    client_secret: 'Client secret',
    from_email: 'From email (sender)',
    region: 'AWS region (e.g. ap-south-1)',
    access_key_id: 'Access key ID',
    secret_access_key: 'Secret access key',
    sender_id: 'Sender ID (optional, e.g. ACCORDIQ)',
    default_country_code: 'Default country code (optional, e.g. 91)',
};

const EMAIL_DESCRIPTION =
    "Microsoft Graph (Outlook) account that sends this app's emails (OTP, invitations, password resets). Fields left empty use the server defaults.";

const inputCls =
    'w-full rounded border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300';

/**
 * One provider's credentials for an app ('graph' email or 'sns' SMS), saved via
 * /api/application/email-config; secrets are write-only. Credentials need a saved
 * app, so they show on edit only.
 */
const EmailProviderSettings = ({ provider, appId, title = 'Email (Microsoft Graph)', description = EMAIL_DESCRIPTION }) => {
    const [state, setState] = useState({ configs: {}, fields: {} });
    const [values, setValues] = useState({});
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!appId) return;
        emailConfigService
            .getEmailConfig(appId)
            .then(setState)
            .catch(() => setMsg({ error: true, text: 'Could not load email settings.' }));
    }, [appId]);

    const fields = state.fields[provider] || { settings: [], secrets: [] };
    const saved = state.configs[provider] || { settings: {}, has_secret: false };
    useEffect(() => {
        setValues({ ...saved.settings });
        setMsg(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider, state]);

    const save = async () => {
        setSaving(true);
        setMsg(null);
        try {
            const pick = (keys) => Object.fromEntries(keys.map((k) => [k, values[k] || '']));
            await emailConfigService.saveEmailConfig(appId, provider, pick(fields.settings), pick(fields.secrets));
            setState(await emailConfigService.getEmailConfig(appId));
            setMsg({ error: false, text: 'Settings saved.' });
        } catch (err) {
            setMsg({ error: true, text: err?.response?.data?.message || 'Could not save settings.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-slate-100 rounded-xl p-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">{title}</label>
            <p className="text-xs text-slate-400 mb-3">{description}</p>

            {!appId ? (
                <p className="text-xs text-slate-400 mt-3">Save the app first to enter credentials.</p>
            ) : (
                <div className="mt-3 space-y-2">
                    {[...fields.settings, ...fields.secrets].map((k) => {
                        const secret = fields.secrets.includes(k);
                        return (
                            <div key={k}>
                                <label className="block text-xs text-slate-500 mb-1">{LABELS[k] || k}</label>
                                <input
                                    type={secret ? 'password' : 'text'}
                                    autoComplete={secret ? 'new-password' : 'off'}
                                    value={values[k] || ''}
                                    placeholder={secret && saved.has_secret ? 'Saved — leave blank to keep' : ''}
                                    onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                        );
                    })}
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm disabled:opacity-50"
                        >
                            {saving ? 'Saving…' : 'Save credentials'}
                        </button>
                        {msg && (
                            <span className={`text-xs ${msg.error ? 'text-red-600' : 'text-emerald-600'}`}>{msg.text}</span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailProviderSettings;
